import { Injectable, inject } from '@angular/core';
import { LocalStorageKey, LocalStorageService } from './local-storage.service';

/**
 * Preferences stored per table. Extend this interface when adding new
 * per-table settings (e.g. visible columns, page size, …).
 */
export type TablePreferences = {
    sort?: {
        key: string;
        ascending: boolean;
    };
    scrollPosition?: number;
    searchPhrase?: string;
    currentPage?: number;
    pageSize?: number;
    // Future: columns?: string[];
};

/** Union of all known table IDs – add a new entry when a new list page needs preferences. */
export type TableId =
    | 'measurement-list'
    | 'location-list'
    | 'device-list'
    | 'user-list'
    | 'tenant-list'
    | 'firmware-list';

type PreferencesStore = Partial<Record<TableId, TablePreferences>>;

@Injectable({
    providedIn: 'root'
})
export class TablePreferencesService {
    private readonly localStorageService = inject(LocalStorageService);

    /** Returns the stored preferences for a specific table, or null if none are stored. */
    public getPreferences(tableId: TableId): TablePreferences | null {
        const store = this.localStorageService.getItem<PreferencesStore>(LocalStorageKey.TablePreferences);
        return store?.[tableId] ?? null;
    }

    /** Persists a partial preferences update for a specific table, merging with any existing values. */
    public setPreferences(tableId: TableId, preferences: Partial<TablePreferences>): void {
        const store = this.localStorageService.getItem<PreferencesStore>(LocalStorageKey.TablePreferences) ?? {};
        store[tableId] = { ...store[tableId], ...preferences };
        this.localStorageService.setItem(LocalStorageKey.TablePreferences, store);
    }

    /** Clears all stored preferences for a specific table. */
    public clearPreferences(tableId: TableId): void {
        const store = this.localStorageService.getItem<PreferencesStore>(LocalStorageKey.TablePreferences) ?? {};
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [tableId]: _, ...rest } = store;
        this.localStorageService.setItem(LocalStorageKey.TablePreferences, rest);
    }

    /** Clears all stored table preferences, e.g. on logout. */
    public clearAll(): void {
        this.localStorageService.removeItem(LocalStorageKey.TablePreferences);
    }
}
